import { Fragment, HTMLAttributes, ReactNode } from 'react';
import styled from '@emotion/styled';
import { Divider } from 'antd';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';

const Platform = styled.div<{ active: string }>`
  color: ${({ active }) => (active === 'true' ? 'var(--theme-text-1)' : 'var(--theme-text-3)')};
`;

export interface PlatformSelectorProps extends HTMLAttributes<HTMLDivElement> {
  activePlatform: General.PlatformUpperCase;
  onActivePlatformChange: (platform: General.PlatformUpperCase) => void;
  platformList?: General.PlatformUpperCase[];
  platformRender?: (platform: General.PlatformUpperCase, label: string) => ReactNode;
}

export const defaultPlatformRender = (platform: General.PlatformUpperCase, label: string) => label;

function PlatformSelector(props: PlatformSelectorProps) {
  const {
    activePlatform,
    onActivePlatformChange,
    platformList = ['SHOPEE', 'LAZADA'],
    platformRender = defaultPlatformRender,
    ...otherProps
  } = props;

  return (
    <div role="tablist" className="flex flex-wrap items-center px-4 py-3 bg-fill-0 gap-3" {...otherProps}>
      {platformList.map((platform, index) => {
        const active = platform === activePlatform;
        const node = (
          <Platform
            className="text-base cursor-pointer"
            key={platform}
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            data-node-key={platform}
            active={String(active)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (!active) onActivePlatformChange(platform);
              }
            }}
            onClick={() => {
              if (!active) {
                onActivePlatformChange(platform);
              }
            }}
          >
            {platformRender(platform, platformLabelMap[toLowerCase(platform)])}
          </Platform>
        );

        if (index === platformList.length - 1) {
          return node;
        }

        return (
          <Fragment key={platform}>
            {node}
            <Divider type="vertical" />
          </Fragment>
        );
      })}
    </div>
  );
}

export default PlatformSelector;
